/**
 * Stage2DetailSection (Plan 04-07) — the HR Stage-2 decision-support block on the
 * candidate detail page. Renders ONLY when the candidate has reached Stage 2
 * (stage in stage2_done | finalist | hired). Composes the inline video (BUG-005),
 * quick-tag, rubric editor, promote/demote, and the PDF/CSV pulls.
 *
 * promote (stage2_done → finalist) is HR-role gated; demote (finalist → stage2_done)
 * is restricted (hr_recruiter cannot) — mirrored from the backend FIN-06/07 rules via
 * canMutate; the backend enforces authoritatively regardless.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useAuthStore } from "@/lib/auth";
import { canMutate } from "@/lib/permissions";
import { mapErrorSlug } from "@/lib/error-slug-mapper";

import type { CandidateDetail } from "./api";
import { InlineVideoPlayer } from "./InlineVideoPlayer";
import { QuickTagControl } from "./QuickTagControl";
import { RubricEditor } from "./RubricEditor";
import { PdfCsvButtons } from "./PdfCsvButtons";
import {
  useDemoteFinalistMutation,
  usePromoteFinalistMutation,
  useSetHiredMutation,
  useUpdateHiringStatusNotesMutation,
} from "./hooks";

const STAGE2_VISIBLE = ["stage2_done", "finalist", "hired"];

export function Stage2DetailSection({ d }: { d: CandidateDetail }) {
  const { t } = useTranslation("rubric");
  const role = useAuthStore((s) => s.user)?.role;
  const promote = usePromoteFinalistMutation(d.id);
  const demote = useDemoteFinalistMutation(d.id);
  const setHiredMut = useSetHiredMutation(d.id);

  if (!STAGE2_VISIBLE.includes(d.stage)) return null;

  async function run(fn: () => Promise<unknown>, okMsg: string): Promise<void> {
    try {
      await fn();
      toast.success(okMsg);
    } catch (err) {
      toast.error(mapErrorSlug(err));
    }
  }

  return (
    <Card data-testid="stage2-detail-section">
      <CardContent className="py-4 space-y-4">
        <h2 className="font-semibold">{t("section.title")}</h2>

        <InlineVideoPlayer candidateId={d.id} hasVideo={d.has_video} />

        <QuickTagControl candidateId={d.id} currentTag={d.shortlist_tag} />

        <RubricEditor
          candidateId={d.id}
          positionId={d.position_id}
          rubricConfig={d.rubric_config}
          savedRubric={d.stage2_rubric}
        />

        <div className="flex flex-wrap items-center gap-3 border-t pt-3">
          {d.stage === "stage2_done" && canMutate("candidate", "advance", role) ? (
            <Button
              onClick={() => run(() => promote.mutateAsync(), t("promote.success"))}
              disabled={promote.isPending}
              className="min-h-[44px]"
              data-testid="promote-btn"
            >
              {t("promote.label")}
            </Button>
          ) : null}
          {d.stage === "finalist" && canMutate("candidate", "unreject", role) ? (
            <Button
              variant="outline"
              onClick={() => run(() => demote.mutateAsync(), t("demote.success"))}
              disabled={demote.isPending}
              className="min-h-[44px]"
              data-testid="demote-btn"
            >
              {t("demote.label")}
            </Button>
          ) : null}
          {/* OPS-04: HR set-hired (finalist → hired, terminal). Same role gate as
              demote (FINALIST_REVERSE_ROLES); backend enforces authoritatively. */}
          {d.stage === "finalist" && canMutate("candidate", "unreject", role) ? (
            <Button
              onClick={() => run(() => setHiredMut.mutateAsync(), t("set_hired.success"))}
              disabled={setHiredMut.isPending}
              className="min-h-[44px]"
              data-testid="set-hired-btn"
            >
              {t("set_hired.label")}
            </Button>
          ) : null}
          {d.stage === "hired" ? (
            <span
              className="rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground"
              data-testid="hired-badge"
            >
              {t("set_hired.hired_badge")}
            </span>
          ) : null}
          <PdfCsvButtons candidateId={d.id} positionId={d.position_id} />
        </div>

        {/* OPS-04: HR-editable hiring_status_notes (Stefan-visible on the overview).
            Available once the candidate is a finalist (or hired). */}
        {d.stage === "finalist" || d.stage === "hired" ? (
          <HiringNotesEditor candidateId={d.id} initial={d.hiring_status_notes} />
        ) : null}
      </CardContent>
    </Card>
  );
}

function HiringNotesEditor({
  candidateId,
  initial,
}: {
  candidateId: number;
  initial: string | null;
}) {
  const { t } = useTranslation("rubric");
  const [notes, setNotes] = useState(initial ?? "");
  const mutation = useUpdateHiringStatusNotesMutation(candidateId);

  async function save() {
    try {
      await mutation.mutateAsync(notes);
      toast.success(t("hiring_notes.saved"));
    } catch (err) {
      toast.error(mapErrorSlug(err));
    }
  }

  return (
    <div className="space-y-2 border-t pt-3" data-testid="hiring-notes-editor">
      <label
        className="text-sm font-medium"
        htmlFor={`hiring-notes-${candidateId}`}
      >
        {t("hiring_notes.label")}
      </label>
      <Textarea
        id={`hiring-notes-${candidateId}`}
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder={t("hiring_notes.placeholder")}
        className="text-base"
      />
      <Button
        onClick={save}
        disabled={mutation.isPending}
        className="min-h-[44px]"
        data-testid="hiring-notes-save"
      >
        {t("hiring_notes.save")}
      </Button>
    </div>
  );
}
